<template>
	<div class="min-h-screen bg-gray-50 p-2 sm:p-4 md:p-6 lg:p-8">
		<div class="max-w-5xl mx-auto">
			<!-- Header Section -->
			<div class="text-center mb-4 lg:mb-8">
				<div
					class="inline-flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 bg-primary rounded-xl mb-2 sm:mb-4 shadow-sm"
				>
					<i class="fas fa-check text-white"></i>
				</div>
				<h1
					class="text-lg sm:text-2xl md:text-3xl font-bold text-gray-900 mb-1 sm:mb-3"
				>
					Vehicle Information
				</h1>
				<p
					class="text-xs sm:text-base text-gray-600 max-w-3xl mx-auto px-2 sm:px-0"
				>
					Please provide accurate vehicle details for verification and
					accurate insurance pricing
				</p>
			</div>

			<form
				@submit.prevent="submitDetails"
				class="space-y-3 sm:space-y-6"
			>
				<!-- Vehicle Identity Card -->
				<div
					class="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-5"
				>
					<div class="flex items-center mb-2 sm:mb-4">
						<div
							class="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-lg mr-2 sm:mr-3"
						>
							<i class="fas fa-home text-blue-600"></i>
						</div>
						<h3
							class="text-xs sm:text-base font-semibold text-gray-800"
						>
							Vehicle Identity
						</h3>
					</div>

					<div
						class="grid grid-cols-1 gap-2 sm:gap-4 sm:grid-cols-2 md:grid-cols-3"
					>
						<!-- Make Dropdown -->
						<div class="md:col-span-1">
							<label
								for="make"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Make
								<span
									class="text-red-500 ml-1"
									v-if="errors.make"
									>*</span
								>
							</label>
							<div class="relative">
								<select
									id="make"
									v-model="riskData.make"
									@blur="validateField('make')"
									class="w-full appearance-none bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
									:class="{ 'border-red-500': errors.make }"
									aria-describedby="make-error"
								>
									<option value="" disabled>
										Select Make
									</option>
									<option
										v-for="car in cars"
										:key="car.brand"
										:value="car.brand"
									>
										{{ car.brand }}
									</option>
								</select>
								<div
									class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
								>
									<i
										class="fas fa-chevron-down text-gray-400"
									></i>
								</div>
							</div>
							<p
								v-if="errors.make"
								id="make-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.make }}
							</p>
						</div>

						<!-- Model Dropdown -->
						<div class="md:col-span-1">
							<label
								for="model"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Model
								<span
									class="text-red-500 ml-1"
									v-if="errors.model"
									>*</span
								>
							</label>
							<div class="relative">
								<select
									id="model"
									v-model="riskData.model"
									@blur="validateField('model')"
									:disabled="!riskData.make"
									class="w-full appearance-none bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
									:class="{ 'border-red-500': errors.model }"
									aria-describedby="model-error"
								>
									<option value="" disabled>
										Select Model
									</option>
									<option
										v-for="model in availableModels"
										:key="model"
										:value="model"
									>
										{{ model }}
									</option>
								</select>
								<div
									class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
								>
									<i
										class="fas fa-chevron-down text-gray-400"
									></i>
								</div>
							</div>
							<p
								v-if="errors.model"
								id="model-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.model }}
							</p>
						</div>

						<!-- Registration Details -->
						<div class="md:col-span-1">
							<label
								for="regNo"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Registration Number
								<span
									class="text-red-500 ml-1"
									v-if="errors.registrationNumber"
									>*</span
								>
							</label>
							<input
								type="text"
								id="regNo"
								v-model="riskData.registrationNumber"
								@blur="validateField('registrationNumber')"
								placeholder="e.g., KAA 123A"
								class="w-full bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{
									'border-red-500': errors.registrationNumber,
								}"
								aria-describedby="regNo-error"
							/>
							<p
								v-if="errors.registrationNumber"
								id="regNo-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.registrationNumber }}
							</p>
							<p class="mt-1 text-xs text-gray-500">
								As shown on your vehicle documents
							</p>
						</div>
					</div>
				</div>

				<!-- Technical Details Card -->
				<div
					class="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-5"
				>
					<div class="flex items-center mb-2 sm:mb-4">
						<div
							class="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-lg mr-2 sm:mr-3"
						>
							<i class="fas fa-bolt text-blue-600"></i>
						</div>
						<h3
							class="text-xs sm:text-base font-semibold text-gray-800"
						>
							Technical Details
						</h3>
					</div>

					<div
						class="grid grid-cols-1 gap-2 sm:gap-4 sm:grid-cols-2 md:grid-cols-2"
					>
						<div>
							<label
								for="chassisNo"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Chassis Number
								<span
									class="text-red-500 ml-1"
									v-if="errors.chassisNumber"
									>*</span
								>
							</label>
							<input
								type="text"
								id="chassisNo"
								v-model="riskData.chassisNumber"
								@blur="validateField('chassisNumber')"
								placeholder="Enter chassis number"
								class="w-full bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{
									'border-red-500': errors.chassisNumber,
								}"
								aria-describedby="chassisNo-error"
							/>
							<p
								v-if="errors.chassisNumber"
								id="chassisNo-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.chassisNumber }}
							</p>
							<p class="mt-1 text-xs text-gray-500">
								Usually found on the vehicle frame or documents
							</p>
						</div>

						<div v-if="!isThirdParty">
							<label
								for="engineNo"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Engine Number
								<span
									class="text-red-500 ml-1"
									v-if="errors.engineNumber"
									>*</span
								>
							</label>
							<input
								type="text"
								id="engineNo"
								v-model="riskData.engineNumber"
								@blur="validateField('engineNumber')"
								placeholder="Enter engine number"
								class="w-full bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{
									'border-red-500': errors.engineNumber,
								}"
								aria-describedby="engineNo-error"
							/>
							<p
								v-if="errors.engineNumber"
								id="engineNo-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.engineNumber }}
							</p>
						</div>

						<div v-if="!isThirdParty">
							<label
								for="engineCapacity"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Engine Capacity (cc)
								<span
									class="text-red-500 ml-1"
									v-if="errors.engineCapacity"
									>*</span
								>
							</label>
							<input
								type="number"
								id="engineCapacity"
								v-model.number="riskData.engineCapacity"
								@blur="validateField('engineCapacity')"
								placeholder="e.g., 1500"
								min="0"
								class="w-full bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{
									'border-red-500': errors.engineCapacity,
								}"
								aria-describedby="engineCapacity-error"
							/>
							<p
								v-if="errors.engineCapacity"
								id="engineCapacity-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.engineCapacity }}
							</p>
						</div>

						<div v-if="!isThirdParty">
							<label
								for="fuelType"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Fuel Type
								<span
									class="text-red-500 ml-1"
									v-if="errors.fuelType"
									>*</span
								>
							</label>
							<div class="relative">
								<select
									id="fuelType"
									v-model="riskData.fuelType"
									@blur="validateField('fuelType')"
									class="w-full appearance-none bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
									:class="{
										'border-red-500': errors.fuelType,
									}"
									aria-describedby="fuelType-error"
								>
									<option value="" disabled>
										Select Fuel Type
									</option>
									<option value="Petrol">Petrol</option>
									<option value="Diesel">Diesel</option>
									<option value="Electric">Electric</option>
									<option value="Hybrid">Hybrid</option>
								</select>
								<div
									class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
								>
									<i
										class="fas fa-chevron-down text-gray-400"
									></i>
								</div>
							</div>
							<p
								v-if="errors.fuelType"
								id="fuelType-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.fuelType }}
							</p>
						</div>
					</div>
				</div>

				<!-- Body & Usage Card -->
				<div
					class="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-5"
				>
					<div class="flex items-center mb-2 sm:mb-4">
						<div
							class="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-lg mr-2 sm:mr-3"
						>
							<i class="fas fa-car text-blue-600"></i>
						</div>
						<h3
							class="text-xs sm:text-base font-semibold text-gray-800"
						>
							Body & Usage
						</h3>
					</div>

					<div
						class="grid grid-cols-1 gap-2 sm:gap-4 sm:grid-cols-2 md:grid-cols-2"
					>
						<div v-if="!isThirdParty">
							<label
								for="bodyType"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Body Type
								<span
									class="text-red-500 ml-1"
									v-if="errors.bodyType"
									>*</span
								>
							</label>
							<input
								type="text"
								id="bodyType"
								v-model="riskData.bodyType"
								@blur="validateField('bodyType')"
								placeholder="SUV, Sedan, etc."
								class="w-full bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{ 'border-red-500': errors.bodyType }"
								aria-describedby="bodyType-error"
							/>
							<p
								v-if="errors.bodyType"
								id="bodyType-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.bodyType }}
							</p>
						</div>

						<div v-if="!isThirdParty">
							<label
								for="usePurpose"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Use Purpose
								<span
									class="text-red-500 ml-1"
									v-if="errors.usePurpose"
									>*</span
								>
							</label>
							<div class="relative">
								<select
									id="usePurpose"
									v-model="riskData.usePurpose"
									@blur="validateField('usePurpose')"
									class="w-full appearance-none bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
									:class="{
										'border-red-500': errors.usePurpose,
									}"
									aria-describedby="usePurpose-error"
								>
									<option value="" disabled>
										Select Use Purpose
									</option>
									<option value="private">Private</option>
									<option value="commercial">
										Commercial
									</option>
								</select>
								<div
									class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
								>
									<i
										class="fas fa-chevron-down text-gray-400"
									></i>
								</div>
							</div>
							<p
								v-if="errors.usePurpose"
								id="usePurpose-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.usePurpose }}
							</p>
						</div>

						<div v-if="!isThirdParty">
							<label
								for="colour"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Colour
								<span
									class="text-red-500 ml-1"
									v-if="errors.colour"
									>*</span
								>
							</label>
							<input
								type="text"
								id="colour"
								v-model="riskData.colour"
								@blur="validateField('colour')"
								placeholder="blue, red, etc."
								class="w-full bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{ 'border-red-500': errors.colour }"
								aria-describedby="colour-error"
							/>
							<p
								v-if="errors.colour"
								id="colour-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.colour }}
							</p>
						</div>

						<div v-if="!isThirdParty">
							<label
								for="yearOfManufacture"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Year of Manufacture
								<span
									class="text-red-500 ml-1"
									v-if="errors.yearOfManufacture"
									>*</span
								>
							</label>
							<input
								type="number"
								id="yearOfManufacture"
								v-model.number="riskData.yearOfManufacture"
								@blur="validateField('yearOfManufacture')"
								placeholder="e.g, 2020"
								min="1900"
								:max="new Date().getFullYear()"
								class="w-full bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{
									'border-red-500': errors.yearOfManufacture,
								}"
								aria-describedby="yearOfManufacture-error"
							/>
							<p
								v-if="errors.yearOfManufacture"
								id="yearOfManufacture-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.yearOfManufacture }}
							</p>
						</div>
					</div>
				</div>

				<!-- Security & Modifications Card -->
				<div
					class="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-5"
				>
					<div class="flex items-center mb-2 sm:mb-4">
						<div
							class="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-lg mr-2 sm:mr-3"
						>
							<i class="fas fa-lock text-blue-600"></i>
						</div>
						<h3
							class="text-xs sm:text-base font-semibold text-gray-800"
						>
							Security & Modifications
						</h3>
					</div>

					<div
						class="grid grid-cols-1 gap-2 sm:gap-4 sm:grid-cols-2 md:grid-cols-2"
					>
						<div v-if="!isThirdParty">
							<label
								for="antiTheftDevice"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Anti-Theft Device
								<span
									class="text-red-500 ml-1"
									v-if="errors.antiTheftDevice"
									>*</span
								>
							</label>
							<div class="relative">
								<select
									id="antiTheftDevice"
									v-model="riskData.antiTheftDevice"
									@blur="validateField('antiTheftDevice')"
									class="w-full appearance-none bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
									:class="{
										'border-red-500':
											errors.antiTheftDevice,
									}"
									aria-describedby="antiTheftDevice-error"
								>
									<option value="" disabled>
										Select Anti-Theft Device
									</option>
									<option value="yes">Yes</option>
									<option value="no">No</option>
								</select>
								<div
									class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
								>
									<i
										class="fas fa-chevron-down text-gray-400"
									></i>
								</div>
							</div>
							<p
								v-if="errors.antiTheftDevice"
								id="antiTheftDevice-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.antiTheftDevice }}
							</p>
						</div>

						<div
							v-if="
								!isThirdParty &&
								riskData.antiTheftDevice === 'yes'
							"
							class="md:col-span-2"
						>
							<label
								for="antiTheftDetails"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Anti-Theft Device Details
								<span
									class="text-red-500 ml-1"
									v-if="errors.antiTheftDetails"
									>*</span
								>
							</label>
							<textarea
								id="antiTheftDetails"
								v-model="riskData.antiTheftDetails"
								@blur="validateField('antiTheftDetails')"
								placeholder="Describe the anti-theft device"
								class="w-full bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400 resize-none"
								:class="{
									'border-red-500': errors.antiTheftDetails,
								}"
								rows="2"
								aria-describedby="antiTheftDetails-error"
							></textarea>
							<p
								v-if="errors.antiTheftDetails"
								id="antiTheftDetails-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.antiTheftDetails }}
							</p>
						</div>

						<div v-if="!isThirdParty">
							<label
								for="modifications"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Modifications
								<span
									class="text-red-500 ml-1"
									v-if="errors.modifications"
									>*</span
								>
							</label>
							<div class="relative">
								<select
									id="modifications"
									v-model="riskData.modifications"
									@blur="validateField('modifications')"
									class="w-full appearance-none bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
									:class="{
										'border-red-500': errors.modifications,
									}"
									aria-describedby="modifications-error"
								>
									<option value="" disabled>
										Select Modifications
									</option>
									<option value="yes">Yes</option>
									<option value="no">No</option>
								</select>
								<div
									class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
								>
									<i
										class="fas fa-chevron-down text-gray-400"
									></i>
								</div>
							</div>
							<p
								v-if="errors.modifications"
								id="modifications-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.modifications }}
							</p>
						</div>

						<div
							v-if="
								!isThirdParty &&
								riskData.modifications === 'yes'
							"
							class="md:col-span-2"
						>
							<label
								for="modificationDetails"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Modification Details
								<span
									class="text-red-500 ml-1"
									v-if="errors.modificationDetails"
									>*</span
								>
							</label>
							<textarea
								id="modificationDetails"
								v-model="riskData.modificationDetails"
								@blur="validateField('modificationDetails')"
								placeholder="Describe the modifications"
								class="w-full bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400 resize-none"
								:class="{
									'border-red-500':
										errors.modificationDetails,
								}"
								rows="2"
								aria-describedby="modificationDetails-error"
							></textarea>
							<p
								v-if="errors.modificationDetails"
								id="modificationDetails-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.modificationDetails }}
							</p>
						</div>

						<div
							v-if="
								!isThirdParty &&
								riskData.modifications === 'yes'
							"
						>
							<label
								for="modificationDate"
								class="flex items-center text-xs sm:text-sm font-semibold text-gray-700 mb-1"
							>
								Modification Date
								<span
									class="text-red-500 ml-1"
									v-if="errors.modificationDate"
									>*</span
								>
							</label>
							<input
								type="date"
								id="modificationDate"
								v-model="riskData.modificationDate"
								@blur="validateField('modificationDate')"
								class="w-full bg-gray-50 border rounded-lg px-3 py-2.5 text-xs sm:text-sm text-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary hover:border-gray-400"
								:class="{
									'border-red-500': errors.modificationDate,
								}"
								aria-describedby="modificationDate-error"
							/>
							<p
								v-if="errors.modificationDate"
								id="modificationDate-error"
								class="text-xs text-red-500 mt-1"
							>
								{{ errors.modificationDate }}
							</p>
						</div>
					</div>
				</div>

				<!-- Action Buttons -->
				<div
					class="flex flex-col-reverse sm:flex-row justify-between gap-2 sm:gap-4"
				>
					<button
						@click="goBack"
						class="w-full sm:w-auto flex items-center justify-center px-4 py-3 text-xs sm:text-sm text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md font-medium transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-gray-400 focus:ring-offset-1"
					>
						<i class="fas fa-arrow-left mr-1"></i>
						Back
					</button>

					<button
						type="submit"
						:disabled="isFormInvalid"
						class="w-full sm:w-auto bg-primary hover:bg-primary-dark disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-1 focus:ring-primary focus:ring-offset-1 disabled:cursor-not-allowed text-xs sm:text-sm"
					>
						Submit Details
					</button>
				</div>
			</form>
		</div>
	</div>
</template>
<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import Utils from '../../../../../assets/js/utilities';

export default {
	name: 'VehicleDetailsForm',
	props: {
		selectedCoverType: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			cars: [], // List of car brands and models from utilities.js
			availableModels: [], // Models for the selected make
			hasAttemptedSubmit: false,
			errors: {
				make: '',
				model: '',
				registrationNumber: '',
				chassisNumber: '',
				engineNumber: '',
				engineCapacity: '',
				fuelType: '',
				bodyType: '',
				usePurpose: '',
				colour: '',
				yearOfManufacture: '',
				antiTheftDevice: '',
				antiTheftDetails: '',
				modifications: '',
				modificationDetails: '',
				modificationDate: '',
			},
		};
	},
	computed: {
		...mapState('risk', ['riskData']),
		...mapGetters('kyc', ['selectKycData']),
		isThirdParty() {
			return this.selectedCoverType === 'third-party';
		},
		isFormInvalid() {
			// Only check for form validity after submission attempt
			if (!this.hasAttemptedSubmit) return false;

			return (
				Object.values(this.errors).some((error) => error !== '') ||
				(this.isThirdParty
					? !this.riskData.make ||
						!this.riskData.model ||
						!this.riskData.registrationNumber ||
						!this.riskData.chassisNumber
					: !this.riskData.make ||
						!this.riskData.model ||
						!this.riskData.registrationNumber ||
						!this.riskData.chassisNumber ||
						!this.riskData.engineNumber ||
						!this.riskData.engineCapacity ||
						!this.riskData.fuelType ||
						!this.riskData.bodyType ||
						!this.riskData.usePurpose ||
						!this.riskData.colour ||
						!this.riskData.yearOfManufacture ||
						!this.riskData.antiTheftDevice ||
						(this.riskData.antiTheftDevice === 'yes' &&
							!this.riskData.antiTheftDetails) ||
						!this.riskData.modifications ||
						(this.riskData.modifications === 'yes' &&
							(!this.riskData.modificationDetails ||
								!this.riskData.modificationDate)))
			);
		},
	},
	watch: {
		'riskData.make': function (newVal) {
			if (newVal) {
				this.updateModels();
			}
		},
	},
	methods: {
		...mapActions('risk', ['setRiskData']),

		goBack() {
			this.$router.push({ name: 'Quotation' });
		},

		validateField(field) {
			// Only show real-time errors after user has attempted to submit
			if (!this.hasAttemptedSubmit) return;

			this.errors[field] = '';

			switch (field) {
				case 'make':
					if (!this.riskData.make) {
						this.errors.make = 'Vehicle make is required';
					}
					break;
				case 'model':
					if (!this.riskData.model) {
						this.errors.model = 'Vehicle model is required';
					}
					break;
				case 'registrationNumber':
					if (!this.riskData.registrationNumber) {
						this.errors.registrationNumber =
							'Registration number is required';
					} else if (
						!/^[A-Z]{3}\s?\d{3}[A-Z]$/.test(
							this.riskData.registrationNumber,
						)
					) {
						this.errors.registrationNumber =
							'Please enter a valid registration number (e.g., KAA 123A)';
					}
					break;
				case 'chassisNumber':
					if (!this.riskData.chassisNumber) {
						this.errors.chassisNumber =
							'Chassis number is required';
					} else if (this.riskData.chassisNumber.length < 3) {
						this.errors.chassisNumber =
							'Chassis number should be 3 characters long';
					}
					break;
				case 'engineNumber':
					if (!this.isThirdParty && !this.riskData.engineNumber) {
						this.errors.engineNumber = 'Engine number is required';
					}
					break;
				case 'engineCapacity':
					if (!this.isThirdParty && !this.riskData.engineCapacity) {
						this.errors.engineCapacity =
							'Engine capacity is required';
					} else if (
						!this.isThirdParty &&
						this.riskData.engineCapacity <= 0
					) {
						this.errors.engineCapacity =
							'Engine capacity must be greater than 0';
					}
					break;
				case 'fuelType':
					if (!this.isThirdParty && !this.riskData.fuelType) {
						this.errors.fuelType = 'Fuel type is required';
					}
					break;
				case 'bodyType':
					if (!this.isThirdParty && !this.riskData.bodyType) {
						this.errors.bodyType = 'Body type is required';
					} else if (
						!this.isThirdParty &&
						!/^[a-zA-Z\s]+$/.test(this.riskData.bodyType)
					) {
						this.errors.bodyType =
							'Body type should only contain letters';
					}
					break;
				case 'usePurpose':
					if (!this.isThirdParty && !this.riskData.usePurpose) {
						this.errors.usePurpose = 'Use purpose is required';
					}
					break;
				case 'colour':
					if (!this.isThirdParty && !this.riskData.colour) {
						this.errors.colour = 'Colour is required';
					} else if (
						!this.isThirdParty &&
						!/^[a-zA-Z\s]+$/.test(this.riskData.colour)
					) {
						this.errors.colour =
							'Colour should only contain letters';
					}
					break;
				case 'yearOfManufacture':
					if (
						!this.isThirdParty &&
						!this.riskData.yearOfManufacture
					) {
						this.errors.yearOfManufacture =
							'Year of manufacture is required';
					} else if (
						!this.isThirdParty &&
						(this.riskData.yearOfManufacture < 1900 ||
							this.riskData.yearOfManufacture >
								new Date().getFullYear())
					) {
						this.errors.yearOfManufacture =
							'Year must be between 1900 and ' +
							new Date().getFullYear();
					}
					break;
				case 'antiTheftDevice':
					if (!this.isThirdParty && !this.riskData.antiTheftDevice) {
						this.errors.antiTheftDevice =
							'Anti-theft device selection is required';
					}
					break;
				case 'antiTheftDetails':
					if (
						!this.isThirdParty &&
						this.riskData.antiTheftDevice === 'yes' &&
						!this.riskData.antiTheftDetails
					) {
						this.errors.antiTheftDetails =
							'Anti-theft device details are required';
					} else if (
						!this.isThirdParty &&
						this.riskData.antiTheftDevice === 'yes' &&
						this.riskData.antiTheftDetails &&
						this.riskData.antiTheftDetails.length < 10
					) {
						this.errors.antiTheftDetails =
							'Details must be at least 10 characters';
					}
					break;
				case 'modifications':
					if (!this.isThirdParty && !this.riskData.modifications) {
						this.errors.modifications =
							'Modifications selection is required';
					}
					break;
				case 'modificationDetails':
					if (
						!this.isThirdParty &&
						this.riskData.modifications === 'yes' &&
						!this.riskData.modificationDetails
					) {
						this.errors.modificationDetails =
							'Modification details are required';
					} else if (
						!this.isThirdParty &&
						this.riskData.modifications === 'yes' &&
						this.riskData.modificationDetails &&
						this.riskData.modificationDetails.length < 10
					) {
						this.errors.modificationDetails =
							'Details must be at least 10 characters';
					}
					break;
				case 'modificationDate':
					if (
						!this.isThirdParty &&
						this.riskData.modifications === 'yes' &&
						!this.riskData.modificationDate
					) {
						this.errors.modificationDate =
							'Modification date is required';
					} else if (
						!this.isThirdParty &&
						this.riskData.modifications === 'yes' &&
						this.riskData.modificationDate &&
						new Date(this.riskData.modificationDate) > new Date()
					) {
						this.errors.modificationDate =
							'Modification date cannot be in the future';
					}
					break;
			}
		},

		validateAllFields() {
			const fields = [
				'make',
				'model',
				'registrationNumber',
				'chassisNumber',
			];
			if (!this.isThirdParty) {
				fields.push(
					'engineNumber',
					'engineCapacity',
					'fuelType',
					'bodyType',
					'usePurpose',
					'colour',
					'yearOfManufacture',
					'antiTheftDevice',
					'modifications',
				);
				if (this.riskData.antiTheftDevice === 'yes') {
					fields.push('antiTheftDetails');
				}
				if (this.riskData.modifications === 'yes') {
					fields.push('modificationDetails', 'modificationDate');
				}
			}

			// Validate all fields
			fields.forEach((field) => {
				this.validateField(field);
			});

			return !Object.values(this.errors).some((error) => error !== '');
		},

		submitDetails() {
			// Mark that user has attempted to submit
			this.hasAttemptedSubmit = true;

			// Validate all fields
			if (!this.validateAllFields()) {
				// Use the same notification system as SummaryPage
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Form Validation Error',
					message:
						'Please correct the highlighted errors before submitting.',
					duration: 4000,
				});
				return;
			}

			// Check for required fields based on cover type
			const requiredFields = [
				'make',
				'model',
				'registrationNumber',
				'chassisNumber',
			];

			if (!this.isThirdParty) {
				requiredFields.push(
					'engineNumber',
					'engineCapacity',
					'fuelType',
					'bodyType',
					'usePurpose',
					'colour',
					'yearOfManufacture',
					'antiTheftDevice',
					'modifications',
				);

				// Check conditional fields
				if (
					this.riskData.antiTheftDevice === 'yes' &&
					!this.riskData.antiTheftDetails
				) {
					Event.$emit('notifications:show', {
						type: 'error',
						title: 'Missing Information',
						message: 'Please provide anti-theft device details.',
						duration: 3000,
					});
					return;
				}

				if (this.riskData.modifications === 'yes') {
					if (
						!this.riskData.modificationDetails ||
						!this.riskData.modificationDate
					) {
						Event.$emit('notifications:show', {
							type: 'error',
							title: 'Missing Information',
							message:
								'Please provide modification details and date.',
							duration: 3000,
						});
						return;
					}
				}
			}

			// Check if any required field is empty
			const missingFields = requiredFields.filter(
				(field) => !this.riskData[field],
			);

			if (missingFields.length > 0) {
				const fieldNames = missingFields.map((field) => {
					switch (field) {
						case 'registrationNumber':
							return 'Registration Number';
						case 'chassisNumber':
							return 'Chassis Number';
						case 'engineNumber':
							return 'Engine Number';
						case 'engineCapacity':
							return 'Engine Capacity';
						case 'fuelType':
							return 'Fuel Type';
						case 'bodyType':
							return 'Body Type';
						case 'usePurpose':
							return 'Use Purpose';
						case 'yearOfManufacture':
							return 'Year of Manufacture';
						case 'antiTheftDevice':
							return 'Anti-theft Device';
						default:
							return (
								field.charAt(0).toUpperCase() + field.slice(1)
							);
					}
				});

				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Incomplete Form',
					message: `Please fill in: ${fieldNames.join(', ')}`,
					duration: 4000,
				});
				return;
			}

			// If validation passes, proceed
			this.setRiskData({ ...this.riskData });

			// Show success notification
			Event.$emit('notifications:show', {
				type: 'success',
				title: 'Vehicle Details Saved',
				message:
					'Your vehicle information has been successfully saved.',
				duration: 2000,
			});

			// Navigate to next step
			if (this.selectKycData.kraPin === 'N/A') {
				this.$router.push({ name: 'KYCDetailsForm' });
			} else {
				this.$router.push({ name: 'SummaryPage' });
			}
		},

		updateModels() {
			this.$set(this.riskData, 'model', '');
			this.availableModels = [];
			if (this.riskData.make) {
				const selectedCar = this.cars.find(
					(car) => car.brand === this.riskData.make,
				);
				this.availableModels = selectedCar ? selectedCar.models : [];
				// Clear model error when make is selected
				if (this.hasAttemptedSubmit) {
					this.validateField('make');
				}
			}
		},
	},

	created() {
		const utils = new Utils();
		this.cars = utils.getCars();
		if (this.riskData && this.riskData.make) {
			this.updateModels();
		}
	},

	mounted() {
		console.log('VehicleDetailsForm mounted with:', {
			selectedCoverType: this.selectedCoverType,
			riskData: this.riskData,
		});

		if (!this.riskData || Object.keys(this.riskData).length === 0) {
			this.setRiskData({
				make: '',
				model: '',
				registrationNumber: '',
				chassisNumber: '',
				engineNumber: '',
				engineCapacity: '',
				fuelType: '',
				bodyType: '',
				usePurpose: '',
				colour: '',
				yearOfManufacture: '',
				antiTheftDevice: '',
				antiTheftDetails: '',
				modifications: '',
				modificationDetails: '',
				modificationDate: '',
			});
		}
	},
};
</script>

<style scoped>
@keyframes fade-in-up {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

.animate-fade-in-up {
	animation: fade-in-up 0.4s ease-out forwards;
}

/* Remove number input spinners */
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}
input[type='number'] {
	-moz-appearance: textfield;
}

/* Enhanced focus styles for accessibility */
button:focus,
input:focus,
select:focus,
textarea:focus {
	outline: 2px solid #669bbc;
	outline-offset: 2px;
}

/* Error state animations */
.border-red-500 {
	transition: border-color 0.3s ease;
}

/* Mobile-specific improvements */
@media (max-width: 640px) {
	.min-h-screen {
		min-height: 100vh;
		display: flex;
		flex-direction: column;
	}

	.max-w-5xl {
		width: 100%;
		flex: 1;
	}

	form {
		flex: 1;
		display: flex;
		flex-direction: column;
	}

	select,
	input,
	textarea {
		font-size: 16px; /* Prevents zoom on iOS */
		min-height: 44px; /* Minimum touch target size */
	}

	button {
		min-height: 48px; /* Minimum touch target size */
	}

	.space-y-3 > * + * {
		margin-top: 0.75rem;
	}

	.p-3 {
		padding: 0.75rem;
	}

	.gap-2 {
		gap: 0.5rem;
	}

	.text-xs {
		font-size: 0.75rem;
		line-height: 1rem;
	}
}

/* Additional improvements for very small devices */
@media (max-width: 360px) {
	.text-lg {
		font-size: 1.125rem;
	}

	.p-2 {
		padding: 0.5rem;
	}

	.text-xs {
		font-size: 0.7rem;
	}
}
</style>
